import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Collapse, IconButton, TextField, Typography, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import useTTS from '../../hooks/useTTS';
import { TokenService } from '../../utils/TokenService';
import { formatEventForTTS } from '../../utils/ttsFormatter';
import Map from '../Map/Map';
import MobileMenuPanel from './MobileMenuPanel';
import BottomNavigation from './BottomNavigation';
import SmartEventList from '../SmartEventList/SmartEventList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SavedItemsList from '../Dashboard/UserDashboard/SavedItemsList';
import Dashboard from '../Dashboard/Dashboard';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ExploreIcon from '@mui/icons-material/Explore';
import AppsIcon from '@mui/icons-material/Apps';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import CloseIcon from '@mui/icons-material/Close';
import TTSButton from './TTSButton';

const MobileLayout = ({
  coordinates,
  setCoordinates,
  bounds,
  setBounds,
  childClicked,
  setChildClicked,
  handleMarkerClick,
  handleLocationClick,
  currentView,
  setCurrentView,
  type,
  setType,
  subCategory,
  setSubCategory,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  searchTerm,
  setSearchTerm,
  isSearchExpanded,
  setIsSearchExpanded,
  followList,
  setFollowList,
  userLocation,
  feedLat,
  feedLng,
  feedRadiusKm,
  onSearch,
  searchQuery, // NEW: Search query to pass to SmartEventList
  hasSearched,
  source,              // NEW: Which collections to search
  excludeCategories,   // NEW: Categories to exclude
  categoryFilter,      // NEW: Category filter from tab config (resolved, for SmartEventList)
  selectedCreator,
  setSelectedCreator,
  creatorProfile,
  onCreatorClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSavedPanel, setShowSavedPanel] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);

  const { speak, stop, isSupported, availableVoices, selectedVoiceName, setVoice, audioOutputDevices, selectedOutputDeviceId, setOutputDevice, readingFinished } = useTTS(language);

  // NEW: State to receive items from SmartEventList
  const [displayedItems, setDisplayedItems] = useState([]);

  // Follow states and hooks
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Fetch follow status
  useEffect(() => {
    if (!isAuthenticated || !selectedCreator?.id) {
      setIsFollowing(false);
      return;
    }
    
    const fetchFollowStatus = async () => {
      try {
        const activeToken = TokenService.getAccessToken();
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${selectedCreator.id}/follow-status`, {
          headers: { Authorization: `Bearer ${activeToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setIsFollowing(data.following);
        }
      } catch (err) {
        console.warn('Error fetching follow status:', err);
      }
    };
    
    fetchFollowStatus();
  }, [isAuthenticated, selectedCreator]);

  const handleToggleFollow = async () => {
    if (!isAuthenticated) {
      alert(language === 'TH' ? 'กรุณาเข้าสู่ระบบเพื่อติดตามผู้สร้างสรรค์' : language === 'CN' ? '请登录以关注创建者' : 'Please log in to follow creators');
      return;
    }
    if (followLoading || !selectedCreator?.id) return;
    setFollowLoading(true);
    try {
      const activeToken = TokenService.getAccessToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${selectedCreator.id}/toggle-follow`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${activeToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsFollowing(data.following);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  const creatorName = creatorProfile?.user?.name || (selectedCreator?.name !== 'ผู้โพสต์' && selectedCreator?.name !== 'Poster' && selectedCreator?.name !== '发布者' ? selectedCreator?.name : null) || (language === 'TH' ? 'ผู้ใช้' : language === 'CN' ? '用户' : 'User');
  const avatarLetters = (() => {
    if (creatorProfile?.user?.name) {
      return creatorProfile.user.name.substring(0, 2).toUpperCase();
    }
    if (selectedCreator?.name && selectedCreator.name !== 'ผู้โพสต์' && selectedCreator.name !== 'Poster' && selectedCreator.name !== '发布者') {
      return selectedCreator.name.substring(0, 2).toUpperCase();
    }
    return 'US';
  })();

  // Followed creators list state
  const [savedTab, setSavedTab] = useState('items'); // 'items' or 'following'
  const [followedCreators, setFollowedCreators] = useState([]);
  const [followedCreatorsLoading, setFollowedCreatorsLoading] = useState(false);

  const fetchFollowedCreators = useCallback(async () => {
    if (!isAuthenticated) return;
    setFollowedCreatorsLoading(true);
    try {
      const activeToken = TokenService.getAccessToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/following`, {
        headers: { Authorization: `Bearer ${activeToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFollowedCreators(data);
      }
    } catch (err) {
      console.warn('Error fetching followed creators:', err);
    } finally {
      setFollowedCreatorsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (showSavedPanel && savedTab === 'following') {
      fetchFollowedCreators();
    }
  }, [showSavedPanel, savedTab, fetchFollowedCreators]);

  // Track whether TTS auto-read is enabled (toggled by the button)
  const [isTTSAutoEnabled, setIsTTSAutoEnabled] = useState(false);
  const previousChildClickedRef = useRef(childClicked);

  // NEW: State to preserve event ID when switching between vertical/horizontal list
  const [, setPreservedEventId] = useState(null);

  // NEW: State for expanded place
  const [expandedPlace, setExpandedPlace] = useState(null);

  const handleMobileMenuNavigate = (path) => {
    setShowMobileMenu(false);
    if (path === '/tour-guide') {
      navigate(path, { state: { background: location } });
    } else {
      navigate(path);
    }
  };

  // NEW: Handle info box click to switch tabs and expand item details
  const handleInfoBoxClick = useCallback((place) => {
    // 1. Move to the Explore navigation tab (vertical list view)
    setCurrentView('verticalExplore');
    
    // 2. Find the index in displayedItems to highlight and scroll
    const idx = displayedItems.findIndex(
      (it) => (it.event_id || it.id) === (place.event_id || place.id)
    );
    if (idx !== -1) {
      setChildClicked(idx);
    }
    
    // 3. Show details in expanded mode
    setExpandedPlace(place);
  }, [displayedItems, setCurrentView, setChildClicked]);

  // Helper function to normalize id field (some items use event_id, some use id)
  const getItemId = useCallback((item) => {
    return item?.event_id || item?.id || null;
  }, []);

  // NEW: Handle items updates from SmartEventList
  // Enhanced to restore focus after switching between vertical/horizontal list
  const handleItemsChange = useCallback(
    (items) => {
      // 1. update our local cache of currently shown items
      setDisplayedItems(items);

      // 2. if we were in the middle of a list-style switch, try to restore
      setPreservedEventId((prevId) => {
        if (!prevId) return null;

        const foundIndex = items.findIndex(
          (it) => getItemId(it) === prevId
        );

        if (foundIndex !== -1) {
          // tell parent / state that this is the newly focused item
          setChildClicked(foundIndex);
          // we consumed the preserved id
          return null;
        }

        // if the item is not in this window yet (because of batching),
        // keep the id for the next onItemsChange
        return prevId;
      });
    },
    [getItemId, setChildClicked]
  );

  // NEW: Handle map sync when active item changes in list
  // NOTE: Does NOT set childClicked to prevent feedback loop
  // (onActiveIndexChange already reports the index separately)
  const handleMapSync = useCallback((lat, lng, index) => {
    // Don't let list items far from the user's GPS location override the map center.
    // 2 degrees ≈ 220 km — items further than that won't re-center the map.
    if (userLocation) {
      const dLat = Math.abs(lat - userLocation.lat);
      const dLng = Math.abs(lng - userLocation.lng);
      if (dLat > 2 || dLng > 2) return;
    }
    setCoordinates({ lat, lng });
  }, [setCoordinates, userLocation]);

  // Handler for when active index changes in both lists (kept for compatibility)
  // STEP 6: Ignore active index changes while stabilizing
  const handleActiveIndexChange = useCallback((index, isStabilizing = false, isAuthoritative = false) => {
    if (isStabilizing) {
      // we are in the middle of a window shift → DO NOT update map, DO NOT set childClicked
      console.log('[MobileLayout] (ignored) active index', index, 'because list is stabilizing');
      return;
    }

    // extra guard: sometimes right after shift we get index 0 from a half-baked window
    if (index === 0 && !isAuthoritative) {
      console.log('[MobileLayout] (ignored) weak index 0 after shift');
      return;
    }

    const place = displayedItems[index];
    if (!place) return;

    // select in list
    setChildClicked(index);

    // move map to this event
    setCoordinates({
      lat: place.latitude,
      lng: place.longitude,
    });
  }, [displayedItems, setChildClicked, setCoordinates]);

  // Get selected event ID from childClicked index
  const selectedEventId = childClicked !== null && displayedItems[childClicked]
    ? (displayedItems[childClicked].event_id || displayedItems[childClicked].id)
    : null;
  const isVerticalExplore = currentView === 'verticalExplore';
  const recommendationSheetHeight = 270;
  const bottomNavigationHeight = 60;
  const visibleExploreMapHeight = '20vh';
  const mapControlsBottom = isVerticalExplore ? 16 : recommendationSheetHeight + bottomNavigationHeight + 70;

  const handleTTSToggle = useCallback(() => {
    if (isTTSAutoEnabled) {
      // Disable auto-read mode
      setIsTTSAutoEnabled(false);
      stop();
    } else {
      // Enable auto-read mode
      setIsTTSAutoEnabled(true);
      
      // Play a short silent utterance to unlock speech synthesis on iOS/Safari/macOS
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        const unlockUtterance = new SpeechSynthesisUtterance('');
        window.speechSynthesis.speak(unlockUtterance);
      }

      // If no item is selected, automatically select the first one!
      let targetIndex = childClicked;
      if (targetIndex === null && displayedItems && displayedItems.length > 0) {
        targetIndex = 0;
        setChildClicked(0);
      }

      const focusedItem = displayedItems[targetIndex];
      if (focusedItem) {
        const text = formatEventForTTS(focusedItem, language);
        speak(text);
      }
    }
  }, [isTTSAutoEnabled, stop, speak, displayedItems, childClicked, language, setChildClicked]);

  const handleTTSRestart = useCallback(() => {
    if (!isTTSAutoEnabled) return;
    const idx = childClicked !== null ? childClicked : 0;
    const focusedItem = displayedItems[idx];
    if (focusedItem) {
      const text = formatEventForTTS(focusedItem, language);
      speak(text);
    }
  }, [isTTSAutoEnabled, displayedItems, childClicked, language, speak]);


  // Refs so the auto-advance effect always sees the latest values without
  // adding them as dependencies (which would re-trigger on every scroll).
  const childClickedAutoRef    = useRef(childClicked);
  const displayedItemsAutoRef  = useRef(displayedItems);
  useEffect(() => { childClickedAutoRef.current   = childClicked;   }, [childClicked]);
  useEffect(() => { displayedItemsAutoRef.current = displayedItems; }, [displayedItems]);

  // When reading finishes naturally (not stopped by the user), advance to
  // the next item. SimpleHorizontalList watches childClicked and scrolls
  // automatically, and the existing auto-read effect below re-reads it.
  useEffect(() => {
    if (readingFinished === 0 || !isTTSAutoEnabled) return;

    const current  = childClickedAutoRef.current !== null ? childClickedAutoRef.current : 0;
    const next     = current + 1;
    const items    = displayedItemsAutoRef.current;

    if (next < items.length) {
      setChildClicked(next);
    } else {
      // Reached end of list — turn off auto-read
      setIsTTSAutoEnabled(false);
    }
  }, [readingFinished, isTTSAutoEnabled, setChildClicked]);

  const previousLanguageRef = useRef(language);

  // Auto-read new middle item when scrolling or when language changes (if TTS is enabled)
  useEffect(() => {
    // Skip on initial mount or when childClicked becomes null
    if (previousChildClickedRef.current === null && childClicked === null) {
      return;
    }

    const languageChanged = previousLanguageRef.current !== language;
    previousLanguageRef.current = language;

    // Auto-read if TTS mode is enabled AND (childClicked changed OR language changed)
    if (isTTSAutoEnabled && (childClicked !== previousChildClickedRef.current || languageChanged)) {
      previousChildClickedRef.current = childClicked;

      // Stop current reading
      stop();

      // Start reading current item
      if (childClicked !== null && displayedItems[childClicked]) {
        const focusedItem = displayedItems[childClicked];
        const text = formatEventForTTS(focusedItem, language);
        speak(text);
      }
    } else {
      previousChildClickedRef.current = childClicked;
    }
  }, [childClicked, isTTSAutoEnabled, displayedItems, language, speak, stop]);

  const isTTSDisabled = !isSupported || !displayedItems || displayedItems.length === 0;

  const copy = {
    TH: {
      greeting: 'อรุณสวัสดิ์, Alex!',
      headline: 'กรุงเทพฯ คึกคักวันนี้!',
      countSuffix: 'รายการน่าสนใจตอนนี้',
      mood: 'อยากทำอะไร?',
      eat: 'กิน',
      vibe: 'บรรยากาศ',
      explore: 'สำรวจ',
      more: 'เพิ่มเติม',
      busyTitle: 'ย่านคึกคัก',
      busyBody: 'ย่านนี้มีคนเยอะตอนนี้',
      sheetTitle: 'มีอะไรน่าสนใจตอนนี้',
      seeAll: 'ดูทั้งหมด',
      search: 'ค้นหากิจกรรม',
      filters: 'ตัวกรอง'
    },
    CN: {
      greeting: '早上好, Alex!',
      headline: '曼谷今天很热闹!',
      countSuffix: '个当前推荐',
      mood: '你现在想做什么?',
      eat: '吃',
      vibe: '氛围',
      explore: '探索',
      more: '更多',
      busyTitle: '热门区域',
      busyBody: '这个区域现在很热闹',
      sheetTitle: '现在有什么有趣的',
      seeAll: '查看全部',
      search: '搜索活动',
      filters: '筛选'
    },
    EN: {
      greeting: 'Good morning, Alex!',
      headline: 'Bangkok is buzzing today!',
      countSuffix: 'interesting now',
      mood: 'What are you in the mood for?',
      eat: 'Eat',
      vibe: 'Vibe',
      explore: 'Explore',
      more: 'More',
      busyTitle: 'Busy area',
      busyBody: 'This area is very busy now',
      sheetTitle: "What's interesting now",
      seeAll: 'See all',
      search: 'Search events',
      filters: 'Filters'
    }
  }[language] || {};

  const chipItems = [
    { id: 'Reviews', label: copy.eat, icon: <RestaurantIcon fontSize="small" />, color: '#ff7a1a' },
    { id: 'Events', label: copy.vibe, icon: <MusicNoteIcon fontSize="small" />, color: '#e83e8c' },
    { id: 'Explore', label: copy.explore, icon: <ExploreIcon fontSize="small" />, color: '#20a47a' },
    { id: 'Places', label: copy.more, icon: <AppsIcon fontSize="small" />, color: '#202124' }
  ];

  return (
    <Box sx={{
      height: '100vh',
      width: '100vw',
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: '#dbe8f0',
      color: '#111827'
    }}>
      {showMobileMenu && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 5000,
          backgroundColor: '#fff',
          pt: 'env(safe-area-inset-top)'
        }}>
          <MobileMenuPanel
            onClose={() => setShowMobileMenu(false)}
            onNavigate={handleMobileMenuNavigate}
            isAuthenticated={isAuthenticated}
            onSearch={onSearch}
            logout={logout}
          />
        </Box>
      )}

      <Box sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: isVerticalExplore ? `calc(100% - (${visibleExploreMapHeight} + env(safe-area-inset-bottom) + ${bottomNavigationHeight}px))` : 0,
        bottom: isVerticalExplore ? `calc(env(safe-area-inset-bottom) + ${bottomNavigationHeight}px)` : 0,
      }}>
        <Map
          setCoordinates={setCoordinates}
          setBounds={setBounds}
          bounds={bounds}
          coordinates={coordinates}
          places={displayedItems}
          selectedEventId={selectedEventId}
          onMarkerClick={handleMarkerClick}
          userLocation={userLocation}
          onSearch={onSearch}
          language={language}
          controlsBottomOffset={mapControlsBottom}
          disablePopups={isVerticalExplore}
          onInfoBoxClick={handleInfoBoxClick}
          onCreatorClick={onCreatorClick}
        />
      </Box>

      <Box sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.76) 0%, rgba(255,255,255,0.18) 13%, rgba(255,255,255,0) 30%)'
      }} />

      <Box sx={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top) + 10px)',
        left: 'max(12px, env(safe-area-inset-left))',
        right: 'max(12px, env(safe-area-inset-right))',
        zIndex: 20
      }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '34px auto minmax(0, 1fr) 40px',
          gap: 0.55,
          alignItems: 'center',
          py: 0.7,
          pl: 0.75,
          pr: 0.6,
          minHeight: 50,
          borderRadius: '18px',
          backgroundColor: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(14px)',
          boxShadow: '0 10px 26px rgba(31, 41, 55, 0.12)',
          border: '1px solid rgba(255,255,255,0.72)'
        }}>
          <IconButton
            onClick={() => setShowMobileMenu(true)}
            size="small"
            aria-label="Open menu"
            sx={{
              width: 34,
              height: 34,
              color: '#59627d',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'rgba(17,24,39,0.06)' }
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            '& .MuiIconButton-root': {
              width: 34,
              height: 34,
              backgroundColor: 'rgba(17,24,39,0.06)'
            },
            '& svg': { fontSize: 20 }
          }}>
            <TTSButton
              isPlaying={isTTSAutoEnabled}
              isDisabled={isTTSDisabled}
              onToggle={handleTTSToggle}
              audioOutputDevices={audioOutputDevices}
              selectedOutputDeviceId={selectedOutputDeviceId}
              onOutputDeviceChange={setOutputDevice}
              onRestart={handleTTSRestart}
            />
          </Box>
          <Box sx={{ position: 'relative', minWidth: 0 }}>
            <TextField
              value={searchTerm || ''}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value === '') onSearch('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch(searchTerm || '');
              }}
              placeholder={language === 'TH' ? 'ค้นหา' : language === 'CN' ? '搜索' : 'Search'}
              size="small"
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{
                minWidth: 0,
                width: '100%',
                '& input': {
                  fontWeight: 800,
                  fontSize: 15,
                  py: 0.5,
                  pr: searchTerm ? '22px' : 0,
                },
                '& input::placeholder': {
                  color: '#4b5563',
                  opacity: 1,
                  fontWeight: 700,
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={() => { setSearchTerm(''); onSearch(''); }}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#888',
                  padding: '2px',
                }}
                aria-label="Clear search"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
          <IconButton
            onClick={() => setIsSearchExpanded((prev) => !prev)}
            aria-label={isSearchExpanded ? 'Hide search filters' : 'Show search filters'}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: '#fff',
              color: '#111827',
              boxShadow: '0 8px 18px rgba(17, 24, 39, 0.13)',
              '&:hover': { backgroundColor: '#fff' }
            }}
          >
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 28,
                transform: isSearchExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 160ms ease'
              }}
            />
          </IconButton>
        </Box>

        <Collapse in={isSearchExpanded}>
          <Box sx={{
            mt: 1,
            p: 1,
            borderRadius: '18px',
            backgroundColor: 'rgba(255,255,255,0.92)',
            boxShadow: '0 12px 30px rgba(31,41,55,0.14)',
            pointerEvents: 'auto'
          }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 42px', gap: 0.65, alignItems: 'center' }}>
              <TextField
                type="date"
                value={startDate || ''}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                sx={{
                  minWidth: 0,
                  '& .MuiInputBase-root': { height: 42, borderRadius: '14px', backgroundColor: '#fff' },
                  '& input': { px: 1.1, py: 0, fontSize: 14, fontWeight: 500, minWidth: 0 }
                }}
              />
              <TextField
                type="date"
                value={endDate || ''}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                sx={{
                  minWidth: 0,
                  '& .MuiInputBase-root': { height: 42, borderRadius: '14px', backgroundColor: '#fff' },
                  '& input': { px: 1.1, py: 0, fontSize: 14, fontWeight: 500, minWidth: 0 }
                }}
              />
              <IconButton onClick={() => onSearch(searchTerm || '')} sx={{ width: 42, height: 42, backgroundColor: '#6d4cff', color: '#fff', borderRadius: '14px', '&:hover': { backgroundColor: '#5b3bea' } }}>
                <SearchIcon />
              </IconButton>
            </Box>
          </Box>
        </Collapse>

        {!isVerticalExplore && <Box sx={{
          display: 'flex',
          gap: 0.75,
          overflowX: 'auto',
          mt: 0.85,
          pb: 0.2,
          pointerEvents: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}>
          {chipItems.map((chip) => {
            const isActive = type === chip.id;
            return (
              <Box
                key={chip.id}
                onClick={() => {
                  setType(chip.id);
                  if (setSubCategory) setSubCategory('');
                }}
                sx={{
                  flex: '0 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.55,
                  px: 1.25,
                  py: 0.85,
                  minWidth: 76,
                  borderRadius: 999,
                  backgroundColor: isActive ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.82)',
                  color: '#111827',
                  border: isActive ? `1.5px solid ${chip.color}` : '1px solid rgba(17,24,39,0.08)',
                  boxShadow: isActive ? '0 8px 20px rgba(17,24,39,0.12)' : '0 5px 14px rgba(17,24,39,0.08)',
                  backdropFilter: 'blur(12px)',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ color: chip.color, display: 'grid' }}>
                  {chip.icon}
                </Box>
                <Typography sx={{ fontWeight: 850, fontSize: 12, lineHeight: 1 }}>
                  {chip.label}
                </Typography>
              </Box>
            );
          })}
        </Box>}
      </Box>

      <Box sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        overflow: 'hidden',
        pointerEvents: 'auto',
        ...(isVerticalExplore ? {
          top: 0,
          bottom: `calc(env(safe-area-inset-bottom) + ${bottomNavigationHeight}px + ${visibleExploreMapHeight})`,
          zIndex: 17,
          pt: 'calc(env(safe-area-inset-top) + 70px)',
          backgroundColor: 'rgba(255,255,255,0.96)',
          boxShadow: '0 12px 34px rgba(17,24,39,0.16)',
        } : {
          bottom: `calc(env(safe-area-inset-bottom) + ${bottomNavigationHeight}px)`,
          zIndex: 16,
          height: 'min(36vh, 320px)',
          minHeight: recommendationSheetHeight,
          borderRadius: '20px 20px 0 0',
          backgroundColor: 'rgba(255,255,255,0.94)',
          boxShadow: '0 -10px 30px rgba(31,41,55,0.14)',
          pt: 0,
        })
      }}
        onWheel={() => setFollowList(true)}
        onTouchStart={() => setFollowList(true)}
      >
        {!isVerticalExplore && <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 0.55, pb: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalFireDepartmentIcon sx={{ color: '#ff4d00', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 850, fontSize: 15 }}>
              {copy.sheetTitle}
            </Typography>
          </Box>
        </Box>}

        {/* Creator Info Card / Banner */}
        {selectedCreator && (
          <Box sx={{
            p: 1,
            mx: 1.5,
            my: 1,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(109, 40, 217, 0.03) 100%)',
            border: '1px solid rgba(124, 58, 237, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            boxShadow: '0 2px 10px rgba(124, 58, 237, 0.04)',
            flexShrink: 0
          }}>
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: '#7c3aed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 800,
              fontSize: avatarLetters.length > 1 ? 12 : 14,
              overflow: 'hidden'
            }}>
              {creatorProfile?.user?.avatar_url ? (
                <img 
                  src={creatorProfile.user.avatar_url} 
                  alt={creatorName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                avatarLetters
              )}
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {language === 'TH' ? 'ผู้โพสต์ / ผู้สร้างสรรค์' : language === 'CN' ? '发布者 / 创建者' : 'CREATOR / POSTER'}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {creatorName}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#7c3aed', fontWeight: 650 }}>
                📍 {displayedItems?.length || 0} {language === 'TH' ? 'รายการน่าสนใจ' : language === 'CN' ? '个推荐项目' : 'items'}
              </Typography>
            </Box>

            {/* Follow Button */}
            {selectedCreator?.id && (
              <Button
                variant={isFollowing ? "outlined" : "contained"}
                size="small"
                onClick={handleToggleFollow}
                disabled={followLoading}
                sx={{
                  borderRadius: '20px',
                  fontSize: '0.65rem',
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 0.3,
                  px: 1.5,
                  flexShrink: 0,
                  height: '24px',
                  ...(isFollowing ? {
                    borderColor: '#7c3aed',
                    color: '#7c3aed',
                    '&:hover': {
                      borderColor: '#6d28d9',
                      backgroundColor: 'rgba(124, 58, 237, 0.04)'
                    }
                  } : {
                    backgroundColor: '#7c3aed',
                    color: '#ffffff',
                    boxShadow: '0 2px 6px rgba(124, 58, 237, 0.2)',
                    '&:hover': {
                      backgroundColor: '#6d28d9',
                      boxShadow: '0 4px 10px rgba(124, 58, 237, 0.3)'
                    }
                  })
                }}
              >
                {followLoading ? '...' : (isFollowing 
                  ? (language === 'TH' ? 'ติดตามแล้ว' : language === 'CN' ? '已关注' : 'Following')
                  : (language === 'TH' ? 'ติดตาม' : language === 'CN' ? '关注' : 'Follow')
                )}
              </Button>
            )}
            
            <IconButton 
              size="small" 
              onClick={() => setSelectedCreator(null)}
              sx={{ p: 0.5, color: 'text.secondary' }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}

        <Box sx={{ height: isVerticalExplore ? (selectedCreator ? 'calc(100% - 64px)' : '100%') : 'calc(100% - 27px)' }}>
          <SmartEventList
            layout={isVerticalExplore ? 'vertical' : 'horizontal'}
            context="explore"
            feedMode={true}
            enabled={feedLat !== null && feedLat !== undefined}
            language={language}
            userLocation={userLocation}
            lat={feedLat}
            lng={feedLng}
            radiusKm={feedRadiusKm ?? undefined}
            searchQuery={searchQuery}
            category={categoryFilter || undefined}
            source={source}
            excludeCategories={excludeCategories}
            startDate={startDate}
            endDate={endDate}
            childClicked={childClicked}
            onLocationClick={handleLocationClick}
            onActiveIndexChange={handleActiveIndexChange}
            onItemsChange={handleItemsChange}
            onMapSyncNeeded={handleMapSync}
            showPager={false}
            hasSearched={hasSearched}
            expandedPlace={expandedPlace}
            onExpand={(place) => setExpandedPlace(place)}
            onExpandClose={() => setExpandedPlace(null)}
          />
        </Box>
      </Box>

      {showSavedPanel && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 'calc(60px + env(safe-area-inset-bottom))',
          zIndex: 20,
          backgroundColor: '#f9fafb',
          display: 'flex',
          flexDirection: 'column',
          pt: 'env(safe-area-inset-top)'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,.08)',
            flexShrink: 0
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => setShowSavedPanel(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.1rem' }}>
                {language === 'TH' ? 'ที่บันทึกไว้' : language === 'CN' ? '已保存' : 'Saved'}
              </Typography>
            </Box>

            {/* Segmented Control Selector Tabs */}
            <Box sx={{
              display: 'flex',
              backgroundColor: '#f1f5f9',
              borderRadius: '20px',
              p: 0.5,
              border: '1px solid #e2e8f0'
            }}>
              <Button
                size="small"
                onClick={() => setSavedTab('items')}
                sx={{
                  borderRadius: '16px',
                  fontSize: '0.78rem',
                  fontWeight: 750,
                  textTransform: 'none',
                  py: 0.4,
                  px: 2,
                  color: savedTab === 'items' ? '#ffffff' : '#475569',
                  backgroundColor: savedTab === 'items' ? '#7c3aed' : 'transparent',
                  boxShadow: savedTab === 'items' ? '0 2px 8px rgba(124, 58, 237, 0.25)' : 'none',
                  '&:hover': {
                    backgroundColor: savedTab === 'items' ? '#6d28d9' : 'rgba(0,0,0,0.04)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                {language === 'TH' ? 'รายการ' : language === 'CN' ? '项目' : 'Items'}
              </Button>
              <Button
                size="small"
                onClick={() => setSavedTab('following')}
                sx={{
                  borderRadius: '16px',
                  fontSize: '0.78rem',
                  fontWeight: 750,
                  textTransform: 'none',
                  py: 0.4,
                  px: 2,
                  color: savedTab === 'following' ? '#ffffff' : '#475569',
                  backgroundColor: savedTab === 'following' ? '#7c3aed' : 'transparent',
                  boxShadow: savedTab === 'following' ? '0 2px 8px rgba(124, 58, 237, 0.25)' : 'none',
                  '&:hover': {
                    backgroundColor: savedTab === 'following' ? '#6d28d9' : 'rgba(0,0,0,0.04)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                {language === 'TH' ? 'ติดตาม' : language === 'CN' ? '关注' : 'Following'}
              </Button>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {savedTab === 'items' ? (
              <SavedItemsList navigate={navigate} language={language} />
            ) : (
              followedCreatorsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
                    {language === 'TH' ? 'กำลังโหลด...' : language === 'CN' ? '正在加载...' : 'Loading...'}
                  </Typography>
                </Box>
              ) : followedCreators.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>👥</Typography>
                  <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.9rem' }}>
                    {language === 'TH' ? 'ยังไม่ได้ติดตามใคร' : language === 'CN' ? '尚未关注任何人' : 'Not following anyone yet'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 1.5 }}>
                  {followedCreators.map((creator) => {
                    const creatorInitials = creator.name ? creator.name.substring(0, 2).toUpperCase() : 'US';
                    return (
                      <Box 
                        key={creator.id}
                        onClick={() => {
                          if (onCreatorClick) {
                            onCreatorClick(creator);
                            setShowSavedPanel(false);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          mb: 1.2,
                          borderRadius: '16px',
                          backgroundColor: '#ffffff',
                          border: '1px solid #f1f5f9',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                            borderColor: '#e2e8f0'
                          }
                        }}
                      >
                        {/* Avatar */}
                        <Box sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          backgroundColor: '#7c3aed',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: 16,
                          boxShadow: '0 2px 6px rgba(124, 58, 237, 0.2)',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          {creator.avatar_url ? (
                            <img 
                              src={creator.avatar_url} 
                              alt={creator.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            creatorInitials
                          )}
                        </Box>

                        {/* Detail */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {creator.name}
                            </Typography>
                            {creator.role && (
                              <Box sx={{
                                px: 0.8,
                                py: 0.1,
                                borderRadius: '10px',
                                backgroundColor: creator.role === 'artist' ? 'rgba(124, 58, 237, 0.08)' : 'rgba(13, 148, 136, 0.08)',
                                border: creator.role === 'artist' ? '1px solid rgba(124, 58, 237, 0.15)' : '1px solid rgba(13, 148, 136, 0.15)'
                              }}>
                                <Typography sx={{
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                  color: creator.role === 'artist' ? '#7c3aed' : '#0d9488',
                                  textTransform: 'uppercase'
                                }}>
                                  {creator.role}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          {creator.bio && (
                            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.2 }}>
                              {creator.bio}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 650, mt: 0.3 }}>
                            📍 {creator.event_count || 0} {language === 'TH' ? 'รายการน่าสนใจ' : language === 'CN' ? '个推荐项目' : 'items'}
                          </Typography>
                        </Box>

                        {/* Action / Unfollow Button */}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={async (e) => {
                            e.stopPropagation(); // prevent clicking card to open creator details
                            try {
                              const activeToken = TokenService.getAccessToken();
                              const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${creator.id}/toggle-follow`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${activeToken}` }
                              });
                              if (res.ok) {
                                // Refresh list
                                fetchFollowedCreators();
                              }
                            } catch (err) {
                              console.error('Error toggling follow from list:', err);
                            }
                          }}
                          sx={{
                            borderRadius: '20px',
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            py: 0.3,
                            px: 1.5,
                            color: '#7c3aed',
                            borderColor: '#7c3aed',
                            textTransform: 'none',
                            flexShrink: 0,
                            '&:hover': {
                              backgroundColor: 'rgba(124, 58, 237, 0.04)',
                              borderColor: '#6d28d9',
                              color: '#6d28d9'
                            }
                          }}
                        >
                          {language === 'TH' ? 'ติดตามแล้ว' : language === 'CN' ? '已关注' : 'Following'}
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )
            )}
          </Box>
        </Box>
      )}

      {showProfilePanel && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 9500, backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', pt: 'env(safe-area-inset-top)', pb: 'env(safe-area-inset-bottom)' }}>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Dashboard onBack={() => setShowProfilePanel(false)} />
          </Box>
        </Box>
      )}

      <Box sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 25,
        pointerEvents: 'auto'
      }}>
        <BottomNavigation
          mode={currentView}
          onHome={() => {
            setCurrentView('home');
            setShowSavedPanel(false);
            setShowProfilePanel(false);
          }}
          onExplore={() => {
            setCurrentView('verticalExplore');
            setShowSavedPanel(false);
            setShowProfilePanel(false);
          }}
          onShowSaved={() => {
            setShowSavedPanel(true);
            setShowProfilePanel(false);
          }}
          savedActive={showSavedPanel}
          onShowProfile={() => {
            if (!isAuthenticated) { navigate('/login'); return; }
            setShowSavedPanel(false);
            setShowProfilePanel(true);
          }}
        />
      </Box>
    </Box>
  );
};

export default MobileLayout;
